import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ImagePlusIcon, RefreshCwIcon } from 'lucide-react';
import {
  forwardRef,
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';

export const ImageUploader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    fileUuid?: string | null;
    onFileUuidChange?: (fileUuid: string | null) => void;
  }
>(({ fileUuid, onFileUuidChange, className, ...props }, ref) => {
  const [uploadedFileUuid, setUploadedFileUuid] = useState<string | null>(
    fileUuid ?? null,
  );
  const [preview, setPreview] = useState<string | ArrayBuffer | null>('');
  const [imageName, setImageName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!fileUuid && uploadedFileUuid) {
      // Hide the preview if the file is removed
      setPreview(null);
      setImageName(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUuid]);

  const uploadFile = useCallback(
    (file: File) => {
      if (!file) return;

      const formData = new FormData();
      formData.append('content', file);

      setIsLoading(true);

      fetch(`/files/${uploadedFileUuid ?? ''}`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Inertia': 'true',
          'X-Requested-With': 'XMLHttpRequest',
          'X-Xsrf-Token': decodeURIComponent(
            document?.cookie.match(/XSRF-TOKEN=([^;]*)/)?.[1] || '',
          ),
        },
      })
        .then((res) => res.json())
        .then((res: { file_uuid: string }) => {
          setUploadedFileUuid(res.file_uuid);
          onFileUuidChange?.(res.file_uuid);
          setIsLoading(false);
        })
        .catch((err) => {
          setIsLoading(false);
          console.error(err);
        });
    },
    [onFileUuidChange, uploadedFileUuid],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(file);
      setImageName(file.name);
      uploadFile(file);
    },
    [uploadFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'image/png': [],
      'image/jpg': [],
      'image/jpeg': [],
      'image/webp': [],
    },
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        'relative mx-auto box-content flex cursor-pointer flex-col items-center justify-between gap-y-2 rounded-lg border border-input p-3 text-sm text-muted-foreground shadow-sm',
        isLoading && 'opacity-70',
        className,
      )}
      {...props}
      ref={ref}
    >
      {isLoading && (
        <RefreshCwIcon className="absolute left-[calc(50%-1.5rem)] top-[calc(50%-1.5rem)] size-[3rem] animate-spin rounded-full bg-background p-2" />
      )}
      {preview && (
        <img
          src={preview as string}
          alt="Uploaded image"
          className="h-[1px] flex-1 rounded-lg object-contain shadow"
        />
      )}
      <ImagePlusIcon
        className={cn('my-auto size-7', preview ? 'hidden' : 'block')}
      />
      <Input {...getInputProps()} type="file" />
      <p className="flex-shrink-0 text-pretty break-all text-center">
        {isDragActive
          ? 'Thả ảnh vào đây!'
          : (imageName ?? 'Bấm vào để chọn ảnh')}
      </p>
    </div>
  );
});
ImageUploader.displayName = 'ImageUploader';
